import axios from "axios";
import api from "../../lib/axios";

/**
 * UsersService — talks to the users-permissions plugin via the shared
 * interceptor in src/lib/axios.js.
 *
 * Security audit follow-up: this used to ship `REACT_APP_ROOT_KEY` (admin
 * token) in the browser bundle to update arbitrary users via
 * /plugin::users-permissions.user/:id. That admin token has been removed.
 * `updateUserDetails` now hits the new authenticated endpoint
 * PUT /custom-api/me/profile which only ever updates the caller's own row.
 *
 * `getUserDetailsByUsername` and `getUserDetailsByUserId` still need to
 * work pre-login (e.g. during the booking flow) — they fall back to the
 * shared API key when no JWT is present. The medium-term plan is to back
 * those reads with a dedicated public endpoint that returns the minimum
 * needed (existence, role flags) rather than the full user row.
 */

class UsersService {
  async getUserDetailsByUsername(username) {
    const qs = require("qs");
    const query = qs.stringify(
      {
        populate: "MembershipID",
        filters: {
          email: {
            $eq: username,
          },
        },
      },
      {
        encodeValuesOnly: true,
      }
    );

    // A logged-in user may only read their OWN row — the Authenticated role
    // doesn't (and shouldn't) have the broad users `find`. So when a JWT is
    // present, read /users/me (always the caller) and wrap it as a one-element
    // array so callers' `response.data[0]` keeps working. This is what makes
    // the booking form prefill + "no password needed" work when logged in.
    // No JWT (guest booking flow): look up by email via the shared API key.
    const hasJwt = !!localStorage.getItem("token");
    if (hasJwt) {
      const me = await api.get("users/me");
      return { data: me && me.data ? [me.data] : [] };
    }
    return axios.get(process.env.REACT_APP_API_URL + "users?" + query, {
      headers: { Authorization: process.env.REACT_APP_API_KEY },
    });
  }

  async getUserDetailsByUserId(user_id) {
    const hasJwt = !!localStorage.getItem("token");
    if (hasJwt) {
      // Authenticated self-service (profile / change password): return only
      // the caller's own row via /users/me. Avoids needing the broad
      // users-permissions `findOne` permission (which would expose any user).
      return api.get("users/me");
    }
    return axios.get(process.env.REACT_APP_API_URL + "users/" + user_id, {
      headers: { Authorization: process.env.REACT_APP_API_KEY },
    });
  }

  /**
   * Update the currently-logged-in user's profile. The legacy signature
   * accepted (user_id, user_data) and PUT to /plugin::users-permissions.user/:id
   * with REACT_APP_ROOT_KEY. We now ignore user_id entirely — the backend
   * derives the target row from ctx.state.user, so a malicious caller can
   * only ever update their own profile.
   */
  async updateUserDetails(_userIdIgnored, user_data) {
    return api.put("me/profile", user_data);
  }

  async ForgotPassword(user_data) {
    // /auth/forgot-password is a Strapi public endpoint — no token needed.
    return axios.post(
      process.env.REACT_APP_API_URL + "auth/forgot-password",
      user_data
    );
  }

  async ResetPassword(user_data) {
    // /auth/reset-password is a Strapi public endpoint — no token needed.
    return axios.post(
      process.env.REACT_APP_API_URL + "auth/reset-password",
      user_data
    );
  }

  // Checks whether an email belongs to a blocked user. Backed by the
  // /email-status custom endpoint which is public. Returns:
  //   { exists: boolean, blocked: boolean, message: string }
  async checkEmailStatus(email) {
    if (!email) {
      return { exists: false, blocked: false, message: "" };
    }
    try {
      const response = await axios.get(
        process.env.REACT_APP_API_URL +
          "email-status/?email=" +
          encodeURIComponent(email)
      );
      return response.data || { exists: false, blocked: false, message: "" };
    } catch (err) {
      // Soft-fail: backend payAndBook guard is the authoritative gate.
      console.log("checkEmailStatus error:", err);
      return { exists: false, blocked: false, message: "" };
    }
  }
}

export default new UsersService();
